import type { ApiEnvelope, DashboardSummary, IntegrationStatus, NavigationMenu, NavigationResponse, ProductListItem } from "@wjbk/workbench-contracts";

// Production is served behind the same Nginx origin, which proxies /api to the
// workbench service. Keep the default relative so a production build never
// points the user's browser at its own 127.0.0.1.
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

async function get<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {};
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_STAFF_ID) {
    headers["X-Workbench-Staff-Id"] = import.meta.env.VITE_DEV_STAFF_ID;
  }
  const response = await fetch(`${baseUrl}${path}`, { headers, credentials: "include" });
  const body = await response.json() as ApiEnvelope<T>;
  if (response.status === 401 && !location.pathname.startsWith("/login")) location.assign("/login");
  if (!response.ok) throw new Error(body.message || `HTTP ${response.status}`);
  return body.data;
}

async function post<T>(path: string, payload?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (payload !== undefined) headers["Content-Type"] = "application/json";
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_STAFF_ID) {
    headers["X-Workbench-Staff-Id"] = import.meta.env.VITE_DEV_STAFF_ID;
  }
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: payload === undefined ? undefined : JSON.stringify(payload)
  });
  const body = await response.json() as ApiEnvelope<T>;
  if (!response.ok) throw new Error(body.message || `HTTP ${response.status}`);
  return body.data;
}

async function put<T>(path: string, payload: unknown): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload)
  });
  const body = await response.json() as ApiEnvelope<T>;
  if (response.status === 401 && !location.pathname.startsWith("/login")) location.assign("/login");
  if (!response.ok) throw new Error(body.message || `HTTP ${response.status}`);
  return body.data;
}

export const api = {
  login: (username: string, password: string) => post<{ staffId: string; displayName: string; mustChangePassword: boolean }>("/api/auth/login", { username, password }),
  me: () => get<Record<string, unknown>>("/api/auth/me"),
  logout: () => post<boolean>("/api/auth/logout"),
  changePassword: (currentPassword: string, newPassword: string) => post<boolean>("/api/auth/change-password", { currentPassword, newPassword }),
  dashboard: () => get<DashboardSummary>("/api/v1/dashboard/summary"),
  products: () => get<ProductListItem[]>("/api/v1/products"),
  product: (id: string) => get<Record<string, unknown>>(`/api/v1/products/${encodeURIComponent(id)}`),
  integrations: () => get<IntegrationStatus[]>("/api/v1/integrations"),
  navigation: () => get<NavigationResponse>("/api/v1/navigation"),
  navigationConfig: () => get<{ menus: NavigationMenu[]; version: number }>("/api/v1/navigation/config"),
  saveNavigationConfig: (version: number, menus: NavigationMenu[]) =>
    put<{ menus: NavigationMenu[]; version: number }>("/api/v1/navigation/config", { version, menus }),
  bytedeskSession: (
    module: "online" | "management" | "ticket" = "online",
    view?: string
  ) => post<{ launchUrl: string }>("/api/v1/sso/bytedesk/session", { module, view })
};
